<template>
  <section :aria-id class="relative mb-4 rounded bg-bgr p-4 shadow">
    <!-- no PriceData -->
    <!-- TODO: Bei fehlgeschlagenem CheckPrice führt das dazu, dass über die sidebar kein neues Datum ausgewählt werden kann -->
    <div v-if="!vacancies">
      <WebccButton class="w-full" variant="cta" size="lg">
        <span>{{ $t(`${trPrefix}button.labels.NA`) }}</span>
      </WebccButton>
    </div>
    <div v-else>
      <!-- Titel -->
      <p class="border-b-2 pb-2 text-sm" :class="titleClass">
        {{ $t(`${trPrefix}messages.${props.state}`) }}
      </p>
      <!-- Datumsfelder -->
      <div class="mt-6 flex h-12 flex-nowrap">
        <label class="mr-4 flex-shrink text-sm tracking-wider text-txt-weak">
          <!-- Hier haben wir auch Probleme bei der Datumsdarstellung -->
          <WebccInput
            v-cloak
            v-model="checkin"
            name="checkin"
            :label="$t(`${trPrefix}labels.checkin`)"
            icon="site/calendar"
            :placeholder="$t(`${trPrefix}placeholder`)"
            readonly
            @click="handleDateClick(true)"
          />
        </label>
        <label class="text-sm tracking-wider text-txt-weak">
          <WebccInput
            v-cloak
            v-model="checkout"
            name="checkout"
            :label="$t(`${trPrefix}labels.checkout`)"
            icon="site/calendar"
            :placeholder="$t(`${trPrefix}placeholder`)"
            readonly
            @click="handleDateClick(false)"
          />
        </label>
      </div>
      <!-- Pax-Select -->
      <div class="mt-4">
        <label class="text-sm tracking-wider text-txt-weak">
          <DetailsPaxSelector
            v-model="pax"
            v-tooltip="{
              content: !trip.checkin ? tooltips.paxSelect : '',
              html: true,
            }"
            :max="paxUpTo"
            :prefix="trPrefix"
            :label="$t(`${trPrefix}labels.pax`)"
            :disabled="!trip.checkin"
          />
        </label>
      </div>

      <div v-if="price" class="mt-6">
        <div v-if="!trip.checkin && price.checkin" class="text-left text-sm">
          <span>{{ $t(`${trPrefix}eg`) }}</span>
          {{ formatDateRange(getTripDateRange(price)) }}
        </div>
        <!-- PricePerNight -->
        <dl
          v-if="pricePerNight"
          v-cloak
          class="mt-1 flex flex-wrap justify-between text-sm"
        >
          <dt class="mr-2">{{ $t(`${trPrefix}price.base`) }}</dt>
          <dd class="grow text-right text-sm font-normal">
            {{
              toMoney(pricePerNight, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: false,
              })
            }}
          </dd>
        </dl>
        <!-- Price -->
        <div
          v-if="price.price"
          class="flex flex-wrap items-end justify-between align-baseline text-lg font-semibold text-[#676767]"
        >
          <span class="mr-2 text-sm leading-6">{{
            $t(`${trPrefix}price.total`, { duration: duration.toString() })
          }}</span>
          <span class="grow text-right text-[#676767]">
            <span v-if="!trip.checkin" class="text-sm">{{
              $t(`${trPrefix}priceFrom`)
            }}</span>
            {{ toMoney(price.price) }}
          </span>
        </div>
        <!-- SpecialPriceReduction -->
        <div
          v-if="price.reduction"
          class="mt-1 divide-x divide-double text-right text-sm text-hlt-hover"
        >
          <span class="pr-2 line-through">
            {{ toMoney(price.regularPrice ?? 0) }}
          </span>
          <span class="pl-2">
            {{ toMoney(price.reduction) }}
            <span>{{ $t(`${trPrefix}saved`) }}</span>
          </span>
        </div>

        <!-- Nebenkosten-Link und Belegungshinweis -->
        <div v-if="state === CheckPriceState.Available" class="mt-4">
          <p class="text-sm">
            <span>{{ $t(`${trPrefix}priceOccupancy`) }}</span
            >:
            <span>{{ $t(`${trPrefix}person_s`, pax) }}</span>
          </p>

          <p
            class="mt-1 flex items-center cursor-pointer text-sm"
            @click="emit('scrollto', 'costsRef')"
          >
            <span>{{ $t(`${trPrefix}toOverview`) }}</span>
            <span>&nbsp;</span>
            <span>{{ $t(`${trPrefix}additionalCosts`) }}</span>
            <span
              ><WebccIcon
                class="!block h-7 w-7 text-[#676767]"
                name="site/chevron-right"
                filled
            /></span>
          </p>
        </div>
        <WebccFreeCancellationInfo
          :trip="trip"
          :cancel-conditions="cancelConditions"
          class="flex justify-center mt-2"
        />
      </div>
      <!-- buttons -->
      <div
        v-if="bookingAllowed"
        class="m-auto mt-6 w-full text-center text-sm lg:w-5/6"
      >
        <!-- booking button -->
        <WebccButton
          class="w-full"
          variant="cta"
          size="lg"
          :disabled="!price?.price"
          @click="onBookingClick(false)"
        >
          <span>{{ $t(`${trPrefix}button.labels.${state}`) }}</span>
        </WebccButton>
        <!-- option button -->
        <WebccButton
          v-if="optionPossible"
          class="mt-4 w-full"
          variant="cta"
          size="lg"
          @click="onBookingClick(true)"
        >
          <span>{{ $t(`${trPrefix}button.labels.OP`) }}</span>
        </WebccButton>
        <small class="block pt-2 leading-tight text-[#676767]">{{
          $t(`${trPrefix}summary`)
        }}</small>
      </div>
      <WebccLoaderOverlay
        v-if="state === CheckPriceState.Loading"
        absolute
        type="blocks"
        :message="$t(`${trPrefix}messages.LOADING`)"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { formatDateRange } from '~/utils/dates'
import { getTripDateRange } from '~/utils/trip'
import { CheckPriceState } from '~/composables/useCheckPrice'
import type { TranslatedAccommodationWithPrice } from '~/resources/tracking/ecommerce'

const trPrefix = 'www.components.views.details.Sidebar.'

interface Props {
  modelValue?: TripUpdate
  state: CheckPriceState
  price?: AccomPrice
  vacancies?: Trips | null
  paxUpTo?: number
  optionPossible?: boolean
  accommodation: TranslatedAccommodation
  cancelConditions: CancelCondition[]
  bookingAllowed?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({}) as TripUpdate,
  price: undefined,
  vacancies: () => ({}) as Trips,
  paxUpTo: 1,
  optionPossible: false,
  cancelConditions: () => [],
  bookingAllowed: true,
})

const emit = defineEmits<{
  'update:modelValue': [TripUpdate]
  book: [BookingOptions]
  scrollto: [string]
}>()

const { t } = useI18n()

const context = useAppContext()
const { toMoney } = useMoneyFormat(() => context.currency)
const tracking = useTracking()

const ariaId = useId()

const titleClasses: Record<string, string> = {
  [CheckPriceState.Available]: 'border-suc',
  [CheckPriceState.OnRequest]: 'border-wrn',
  [CheckPriceState.OnRequestRP]: 'border-wrn',
  [CheckPriceState.NotAvailable]: 'border-err',
  [CheckPriceState.Example]: 'border-bgr-300',
  [CheckPriceState.Loading]: 'border-bgr-300',
}

const titleClass = computed(() => titleClasses[props.state])
const checkin = computed(() =>
  props.modelValue.checkin
    ? formatDatetime(toDate(props.modelValue.checkin))
    : '',
)
const checkout = computed(() =>
  props.modelValue.checkout
    ? formatDatetime(toDate(props.modelValue.checkout))
    : '',
)
const duration = computed(
  () =>
    getTripDuration(props.modelValue) ||
    props.price?.duration ||
    DURATION_DEFAULT,
)

const pax = computed({
  get() {
    return props.modelValue.pax || props.paxUpTo
  },
  set(value) {
    emit('update:modelValue', { pax: parseInt(value.toString()) })
  },
})

const trip = computed({
  get() {
    return props.modelValue
  },
  set(value) {
    emit('update:modelValue', value)
  },
})

const pricePerNight = computed(() => {
  if (props.price?.price && pax.value && duration.value) {
    return props.price.price / pax.value / duration.value
  }
  return 0
})

const tooltips = computed(() => {
  return {
    costOverview: false,
    paxSelect: `${t(`${trPrefix}paxNoDuration`)}<br />${t(`${trPrefix}paxAfterDuration`)}`,
    currency: 'fake',
  }
})

function handleDateClick(trackDateClick: boolean) {
  if (trackDateClick) {
    tracking.handlers?.detail.sidebarDateClick()
    tracking.handlers?.detail.sidebarArrivalDate(props.modelValue.checkin)
  }

  emit('scrollto', 'vacanciesRef')
}

function onBookingClick(isOption: boolean) {
  if (
    props.state === CheckPriceState.Available ||
    props.state === CheckPriceState.OnRequest ||
    props.state === CheckPriceState.OnRequestRP
  ) {
    tracking.handlers?.ecommerce.addToCart({
      ...props.accommodation,
      ...props.price,
      ...props.modelValue,
    } as TranslatedAccommodationWithPrice)
    emit('book', { isOption })
  } else {
    emit('scrollto', 'vacanciesRef')
  }
}
</script>
